import { AgentAvatarPreview } from "@/components/ui/agent-avatar-preview";
import { getFrameTypeIcon } from "@/components/ui/concept-icons";
import {
  surfaceRoleAttributes,
  surfaceRoleClassName,
} from "@/components/ui/surface-contract";
import { useT } from "@/i18n";
import { canvasCommentsMessages } from "@/i18n/messages/canvas-comments";
import { cn } from "@/lib/cn";
import type { AgentAvatarStyleKey } from "@/lib/dicebear-styles";
import type { CommentMentionOption } from "@/hooks/use-comment-action";
import { denkerAvatarColor } from "@/hooks/use-denker-avatar-color";
import { useProjectStore } from "@/stores/project-store";

type CommentMentionMenuProps = {
  options: CommentMentionOption[];
  avatarStyleKey: AgentAvatarStyleKey;
  highlightedIndex: number;
  onSelect: (option: CommentMentionOption) => void;
  testId?: string;
};

export function CommentMentionMenu({
  options,
  avatarStyleKey,
  highlightedIndex,
  onSelect,
  testId = "comment-popover-mentions",
}: CommentMentionMenuProps) {
  const t = useT(canvasCommentsMessages);
  const project = useProjectStore((state) => state.currentProject);
  return (
    <div
      className={cn(
        surfaceRoleClassName("menu"),
        "scrollbar-none absolute left-0 right-0 top-full z-10 mt-1 max-h-40 overflow-y-auto p-1",
      )}
      data-testid={testId}
      {...surfaceRoleAttributes("menu", {
        nativeLevel: "root",
        nativeGroup: "menus",
      })}
    >
      <div className="border-b border-glass-stroke-subtle px-2.5 py-1.5">
        <span className="text-appkit-mini font-medium text-muted">
          {t("mentionHeading")}
        </span>
      </div>
      {options.length > 0 ? (
        <div className="pt-1">
          {options.map((option, i) => {
            const FrameIcon = getFrameTypeIcon(
              option.kind === "frame" ? option.frame.frame_type : null,
            );
            return (
              <button
                key={
                  option.kind === "agent"
                    ? `agent-${option.agent.id}`
                    : `frame-${option.frame.frame_id}`
                }
                type="button"
                className={cn(
                  "denker-glass-hover flex w-full items-center gap-2 rounded-control-sm px-2.5 py-1.5 text-left text-secondary transition-colors",
                  i === highlightedIndex &&
                    "bg-glass-stroke-light text-primary",
                )}
                onMouseDown={(e) => {
                  e.preventDefault();
                  onSelect(option);
                }}
                data-testid={
                  option.kind === "agent"
                    ? `comment-mention-${option.agent.id}`
                    : `comment-mention-frame-${option.frame.frame_id}`
                }
              >
                {option.kind === "agent" ? (
                  <AgentAvatarPreview
                    styleKey={avatarStyleKey}
                    seed={option.agent.avatar_seed || option.agent.name}
                    backgroundColor={denkerAvatarColor(
                      option.agent.name,
                      project,
                    )}
                    size={24}
                  />
                ) : (
                  <FrameIcon className="h-5 w-5 shrink-0 text-muted" />
                )}
                <span className="truncate text-appkit-mini">
                  {option.kind === "agent"
                    ? option.agent.name
                    : option.frame.label}
                </span>
              </button>
            );
          })}
        </div>
      ) : (
        <p className="px-2.5 py-2 text-center text-appkit-mini text-muted">
          {t("noAgentsFound")}
        </p>
      )}
    </div>
  );
}
